// Side-effect import: the class below is referenced only as a type, and
// without this the bundler drops the module, the custom element never
// registers, and `hydrate` hands back an un-upgraded <std-diagnostic>.
import "@tento-ui/components/accordion";
import "@tento-ui/components/diagnostic";

import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css } from "@tento-ui/component-styles";
import { Typography } from "@tento-ui/ui/typography";
import type { Accordion } from "@tento-ui/components/accordion";
import type { Diagnostic } from "@tento-ui/components/diagnostic";

/** `turn.result` — how the turn ended, at the weight the ending deserves,
 * which for a turn that went fine is NOTHING.
 *
 * ## ok renders nothing, and that is the whole design
 *
 * Neither ChatGPT nor Claude marks a turn that went fine. The reply ends,
 * the action bar appears under it, and that is the entire ceremony. The
 * first revision agreed with that in its own doc comment and then rendered
 * a line anyway — a muted "✓ Done — 4 files changed" under every single
 * turn. Quiet is not the same as absent: a mark that appears every time
 * carries no information the reader did not already have from the reply
 * being there, and once every turn has one the reader stops seeing marks
 * at all, including the one that says something went wrong.
 *
 * So the ok face is `display: none`. The component still ACCEPTS an ok
 * result, because callers project one from the wire and should not have to
 * branch, and it renders zero pixels.
 *
 * One caller does have to branch, and it is worth saying where. A host
 * that measures its rows — `AgentTranscript` — reads a height of 0 as NOT
 * YET MEASURED, because an unmounted cell reports 0 as well, and holds its
 * estimate instead. Mount an ok result as its own virtualized row and you
 * get a row-height gap where the nothing is. Row-per-item hosts skip the
 * ok rather than mounting it.
 *
 * The facts that used to ride that line — files changed, tokens, elapsed —
 * are not lost and were never this primitive's to hold. `turn.bar` already
 * carries turn metadata behind its details button, disclosed without
 * displacing anything, which is the behaviour these rows need in a
 * virtualized transcript where an expanding row shoves the scrollback.
 *
 * ## suspended is the case prior art actually marks
 *
 * A turn that stopped without finishing is the one ending a reader cannot
 * infer from the page. ChatGPT says so and offers "Continue generating";
 * Claude says the response was interrupted. Both make it visible and
 * neither makes it loud — it is a fact about the session, not an error.
 *
 * So suspended keeps a line, in the ambient register `turn.note` uses, and
 * anything more (a truncated tail, a stop reason, a resume token) goes
 * behind the same chevron `turn.thought` and `turn.tool` use. Resuming is
 * an application decision and stays with the caller: `ask` remains the one
 * interactive primitive in this family.
 *
 * "The same chevron" is now literal rather than aspirational. That line is
 * `std-accordion` with one item, as `turn.tool` and `turn.thought` are —
 * the review that said "this is essentially a smaller accordian. can we
 * reuse the accordian and just tweak its values with css variables" was
 * never about one component, and this file had collected six rounds of
 * alignment verdicts for re-deriving the same layout by hand. A button, a
 * chevron div, an `expanded` attribute, a click handler and a rail
 * computed from the chevron's optical centre are all gone; what is left is
 * a header, a body, and the same variable block its two siblings declare.
 *
 * ## error is somebody else's shape
 *
 * Failure is an inline panel with the machine's own output in it, and that
 * panel is `std-diagnostic`, which is not part of this vocabulary at all —
 * a failed spool job and a panicking node deserve the same shape and must
 * not each grow their own red box. */
@component({ name: "std-turn-result" })
export class TurnResult extends LonaWebComponent {
  private $row: Accordion = this.hydrate("row");
  private $diagnostic: Diagnostic = this.hydrate("diagnostic");

  static makeWith(props: TurnResult.Props): TurnResult {
    return (TurnResult.make() as TurnResult).bind(props);
  }

  bind(props: TurnResult.Props): this {
    this.setAttribute("status", props.status);
    if (props.status === "error") {
      this.$diagnostic.bind({ title: props.text, detail: props.detail });
      return this;
    }
    if (props.status === "ok") return this;

    void this.$row.bind({
      items: [{
        header: TurnResult.buildLine(props),
        content: TurnResult.discloses(props)
          ? TurnResult.buildDetail(props.detail ?? "")
          : "",
      }],
      expandedIndexes: TurnResult.discloses(props) && (props.expanded ?? false)
        ? [0]
        : [],
    });
    return this;
  }

  /** Whether what the turn left behind is worth a disclosure.
   *
   * The same test `turn.tool` applies, and for the same reason it was given
   * there: "tool calls that are one line shouldn't be expandable." A chevron
   * over one line costs a click, a hit target and a column to reveal
   * something that would have fitted on the row hiding it. A stop reason is
   * usually one line; a truncated tail is not. So the LINE decides, not the
   * length, and a one-line reason rides the row.
   *
   * A row with nothing to disclose still keeps its chevron COLUMN — the
   * accordion drops only the glyph, the click and the focus stop — which is
   * what lets a suspended result sit in a run of tool calls without the
   * column stepping sideways at its row. */
  private static discloses(props: TurnResult.Props): boolean {
    return (props.detail ?? "").includes("\n");
  }

  /** The suspended line, built as an element because it goes INTO the
   * accordion's shadow root, where a stylesheet written here cannot reach
   * it. Typography's sheet IS in every shadow root, so the ramp class still
   * resolves and only the values travel inline. */
  private static buildLine(props: TurnResult.Props): HTMLElement {
    const $line = document.createElement("div");
    Object.assign($line.style, {
      display: "flex",
      alignItems: "center",
      gap: "6px",
      minWidth: "0",
      flex: "1",
    });
    $line.append(TurnResult.buildMark(props.status));

    const $text = Typography.small(props.text);
    Object.assign($text.style, {
      minWidth: "0",
      color: "var(--text-sec)",
      overflowWrap: "anywhere",
    });
    $line.append($text);

    // A one-line reason is the row's last fact, not a body behind a control.
    const inline = TurnResult.discloses(props) ? "" : props.detail ?? "";
    if (inline !== "") {
      const $dot = Typography.small("·");
      Object.assign($dot.style, {
        flex: "none",
        color: "var(--text-ter)",
        opacity: "0.6",
      });
      const $inline = Typography.small(inline);
      Object.assign($inline.style, {
        flex: "0 1 auto",
        minWidth: "0",
        color: "var(--text-ter)",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      });
      $inline.style.setProperty(
        "--font-size",
        "var(--turn-fine-size, var(--text-8px))",
      );
      $line.append($dot, $inline);
    }
    return $line;
  }

  /** Revised on review: "suspended looks bad".
   *
   * It was a pause glyph in yellow next to a grey line — a pictogram
   * borrowed from a transport control, at 11px, carrying a meaning ("this
   * stopped and could continue") that a transport control does not have. It
   * read as a decoration rather than a state.
   *
   * A stopped turn is the same fact turn.tool marks on a call that never
   * finished, so it takes the same shape: one hollow ring, drawn rather than
   * typed, in the neutral tone. Neutral because this is a fact about the
   * session, not a warning — the yellow was making an interrupted turn look
   * like a failed one, which is the distinction this component exists to
   * keep. */
  private static buildMark(status: TurnResult.Status): HTMLElement {
    const $mark = document.createElement("div");
    $mark.setAttribute("aria-label", status);
    Object.assign($mark.style, {
      flex: "none",
      boxSizing: "border-box",
      width: "var(--tool-mark-size, 10px)",
      height: "var(--tool-mark-size, 10px)",
      borderRadius: "50%",
      border: "1.5px solid var(--text-ter, #8b93a7)",
    });
    return $mark;
  }

  /** What the turn left behind, in the row's own word column and with no
   * rail — the shape `turn.tool` arrived at on the review that said "remove
   * the divider between tool calls. code-block has too much padding-left and
   * is not aligned." A vertical rule needs something on both sides of it to
   * earn its ink, and a disclosed body has nothing to its left but indent.
   *
   * The geometry is on the element, not in --accordion-content-padding,
   * because an ELEMENT handed to the accordion as content BECOMES the padded
   * box — `renderContent` puts `.accordion-content-custom` on the element
   * itself — so the knob and this element's own padding are the same
   * declaration and inline wins. */
  private static buildDetail(detail: string): HTMLElement {
    const textX = "calc(var(--turn-chevron-width, 14px) + 6px +" +
      " var(--tool-mark-size, 10px) + 6px)";
    const $detail = document.createElement("pre");
    $detail.className = "code-block";
    $detail.textContent = detail;
    Object.assign($detail.style, {
      margin: `2px 0 4px ${textX}`,
      padding: "0",
      color: "var(--text-sec)",
      whiteSpace: "pre",
      overflowX: "auto",
      maxHeight: "260px",
      overflowY: "auto",
    });
    $detail.style.setProperty(
      "--font-size",
      "var(--turn-fine-size, var(--text-8px))",
    );
    $detail.style.setProperty("--line-height-ratio", "1.55");
    return $detail;
  }

  static $styles = [
    css`
      /* The error face is a full-width panel; without this the host hugs
         its content wherever a parent sizes children to fit. */
      :host {
        display: block;
        width: 100%;
        min-width: 0;
        /* The same three every row in this family declares, and deliberately
           the same values: a result, a thought and a tool call are rows of
           one transcript, so their chevron columns, their word columns and
           their highlights are one set of numbers rather than three that
           agree today. */
        --turn-row-pad-y: 3px;
        --turn-row-bleed-x: 6px;
        --turn-row-hover-inset-y: 1px;
      }
      /* A turn that went fine is unmarked, the way both vendor surfaces
         leave it. Zero pixels, not a quiet line — see the note above. */
      :host([status="ok"]) {
        display: none;
      }
      :host([status="error"]) #row {
        display: none;
      }
      #row {
        --accordion-header-padding: var(--turn-row-pad-y) 0;
        --accordion-header-gap: 6px;
        --accordion-icon-size: var(--turn-chevron-width, 14px);
        --accordion-icon-svg-size: var(--turn-chevron-size, 10px);
        /* Zero, because the panel IS the padded box - see buildDetail - and
           carries its own indent. */
        --accordion-content-padding: 0px;
        --accordion-header-hover-inset: var(--turn-row-hover-inset-y)
          calc(-1 * var(--turn-row-bleed-x));
      }

      /* ---- the loud face: error, delegated ------------------------- */
      #exception {
        display: none;
      }
      :host([status="error"]) #exception {
        display: block;
      }
    `,
  ];

  static $html: Option<HTMLTemplateElement> = html`
    <div id="root">
      <std-accordion id="row"></std-accordion>
      <std-diagnostic id="diagnostic"></std-diagnostic>
    </div>
  `;
}

export namespace TurnResult {
  export type Status = "ok" | "error" | "suspended";

  export type Props = {
    status: Status;
    /** The one-line outcome: the suspended line, or the exception's
     * message. Ignored when `status` is "ok", which draws nothing. */
    text: string;
    /** Error: the verbatim body handed to `std-diagnostic`. Suspended: what
     * the reader can open — a truncated tail, a stop reason. */
    detail?: string;
    expanded?: boolean;
  };
}
